import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';

export type ModalSize = 'sm' | 'md' | 'lg';

export type ModalProps = {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  size?: ModalSize;
  dialogStyle?: React.CSSProperties;
  closeOnBackdrop?: boolean;
  children?: React.ReactNode;
  footer?: React.ReactNode;
};

const sizeClass: Record<ModalSize, string> = {
  sm: 'max-w-400',
  md: 'max-w-640',
  lg: 'max-w-920',
};

export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  size = 'lg',
  dialogStyle,
  closeOnBackdrop = true,
  children,
  footer,
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const modal = (


    <div className="modal-portal">
      <div className="modal-backdrop" onClick={closeOnBackdrop ? onClose : undefined} />
      <div className={`modal-dialog ${sizeClass[size]} radius-30`} role="dialog" aria-modal="true" style={dialogStyle}>
        <div className="modal-content">
          {(title || typeof onClose === 'function') && (
            <div className="modal-header">
              <div className="modal-title fz-20 fw-600">{title}</div>
              <button aria-label="Close" className="btn-close" onClick={onClose} />
            </div>
          )}
          <div className="modal-body">{children}</div>
          {footer ? <div className="modal-footer">{footer}</div> : null}
        </div>
      </div>
      <style>{`
        .modal-portal{position:fixed;inset:0;z-index:1050;display:flex;align-items:center;justify-content:center;}
        .modal-backdrop{position:absolute;inset:0;background:rgba(0,0,0,.5);}
        .modal-dialog{position:relative;margin:0 auto;background:#fff;color:#111;}
        .modal-content{overflow:hidden;background:#fff;color:#111;}
        .modal-header,.modal-footer{padding:16px 20px;border-bottom:1px solid rgba(0,0,0,.08);} 
        .modal-footer{border-top:1px solid rgba(0,0,0,.08);border-bottom:none;display:flex;gap:12px;justify-content:flex-end}
        .modal-body{padding:20px}
        .btn-close{background:transparent;border:0;width:24px;height:24px;position:relative}
        .btn-close:before,.btn-close:after{content:'';position:absolute;top:50%;left:50%;width:16px;height:2px;background:#333;transform-origin:center}
        .btn-close:before{transform:translate(-50%,-50%) rotate(45deg)}
        .btn-close:after{transform:translate(-50%,-50%) rotate(-45deg)}
        .max-w-400{max-width:400px}
        .max-w-640{max-width:640px}
        .max-w-920{max-width:920px}
      `}</style>
    </div>
  );

  const portalRoot = document.getElementById('modal-root') || (() => {
    const el = document.createElement('div');
    el.id = 'modal-root';
    document.body.appendChild(el);
    return el;
  })();

  return ReactDOM.createPortal(modal, portalRoot);
};

export default Modal;


